import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import VSLogo from "@/components/VSLogo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const DOMAINS = ["Java","Python","AI/ML","Data Analytics","Data Science","MERN","DevOps","Cloud","QA/Testing","Other"];
const EXPERIENCE = ["Fresher","0-1 Years","1-3 Years","3-5 Years","5+ Years"];
const EDUCATION = ["B.Tech / B.E.","BCA","MCA","B.Sc","M.Sc","MBA","M.Tech","Polytechnic / Diploma","Other"];
const COUNTRIES = ["India","United States","United Kingdom","Canada","Australia","UAE","Singapore","Germany","Other"];

const initial = {
  first_name: "", last_name: "", email: "", confirm_email: "",
  password: "", confirm_password: "", phone: "", country: "India",
  preferred_location: "", professional_status: "Fresher", experience_category: "Fresher",
  preferred_domains: [], highest_education: "B.Tech / B.E.",
  linkedin: "", github: "", portfolio: "",
  terms_accepted: false, privacy_accepted: false, marketing_opt_in: false,
};

function pwdStrength(p) {
  let s = 0;
  if (p.length >= 8) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return s;
}

export default function Register() {
  const [f, setF] = useState(initial);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleDomain = (d) => {
    setF((x) => ({ ...x, preferred_domains: x.preferred_domains.includes(d)
      ? x.preferred_domains.filter((y) => y !== d)
      : [...x.preferred_domains, d] }));
  };

  const validateStep1 = () => {
    if (!f.first_name || !f.last_name) return "Please enter your name.";
    if (!f.email || !f.confirm_email) return "Please provide your email.";
    if (f.email.toLowerCase() !== f.confirm_email.toLowerCase()) return "Emails do not match.";
    if (!f.phone) return "Please provide your mobile number.";
    return null;
  };
  const validateStep2 = () => {
    if (pwdStrength(f.password) < 4) return "Password must include uppercase, lowercase, number and be 8+ characters.";
    if (f.password !== f.confirm_password) return "Passwords do not match.";
    return null;
  };
  const validateStep3 = () => {
    if (!f.terms_accepted || !f.privacy_accepted) return "You must accept the Terms & Privacy Policy.";
    return null;
  };

  const next = () => {
    const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
    if (err) { toast.error(err); return; }
    setStep(step + 1);
  };
  const back = () => setStep(Math.max(1, step - 1));

  const submit = async (e) => {
    e.preventDefault();
    const err = validateStep3();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      await api.post("/auth/register-otp", f);
      toast.success("Verification code sent to your email.");
      nav(`/candidate/verify-email?email=${encodeURIComponent(f.email)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const strength = pwdStrength(f.password);
  const strengthLabel = ["Too short","Weak","Fair","Good","Strong","Excellent"][strength];
  const strengthColor = ["bg-slate-200","bg-red-400","bg-orange-400","bg-yellow-400","bg-emerald-400","bg-emerald-500"][strength];

  return (
    <div className="min-h-screen grid md:grid-cols-5 bg-slate-50" data-testid="register-page">
      <div className="hidden md:flex md:col-span-2 vs-hero-gradient text-white p-12 flex-col justify-between">
        <VSLogo dark size={44} showWordmark/>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">Build your future with VS-Solutions</h2>
          <p className="mt-4 text-slate-300 max-w-md">Create your account to explore opportunities, apply for roles, track applications and grow your career with Vijay Software Solutions.</p>
          <div className="mt-10 space-y-3 text-sm">
            {["Job-specific automatic assessments","Personalized career roadmap","VS AI career assistant","Live hackathons & events"].map((t)=>(
              <div key={t} className="flex items-center gap-3"><Check className="w-4 h-4 text-emerald-300"/><span className="text-slate-200">{t}</span></div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-400">© 2026 Vijay Software Solutions Pvt. Ltd.</div>
      </div>

      <div className="md:col-span-3 flex items-start justify-center p-6 md:p-12">
        <div className="w-full max-w-2xl">
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">Create your VS-Solutions Careers account</h1>
          <p className="text-slate-500 mt-2">Step {step} of 3</p>
          <div className="mt-4 flex gap-2">
            {[1,2,3].map((n)=>(
              <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#0A2540]" : "bg-slate-200"}`}/>
            ))}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {step === 1 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>First Name *</Label><Input required value={f.first_name} onChange={e=>set("first_name",e.target.value)} data-testid="register-first-name"/></div>
                  <div><Label>Last Name *</Label><Input required value={f.last_name} onChange={e=>set("last_name",e.target.value)} data-testid="register-last-name"/></div>
                </div>
                <div><Label>Email *</Label><Input type="email" required value={f.email} onChange={e=>set("email",e.target.value)} data-testid="register-email"/></div>
                <div><Label>Confirm Email *</Label><Input type="email" required value={f.confirm_email} onChange={e=>set("confirm_email",e.target.value)} data-testid="register-confirm-email"/></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Mobile Number *</Label><Input required value={f.phone} onChange={e=>set("phone",e.target.value)} data-testid="register-phone"/></div>
                  <div>
                    <Label>Country / Region *</Label>
                    <Select value={f.country} onValueChange={(v)=>set("country",v)}>
                      <SelectTrigger data-testid="register-country"><SelectValue/></SelectTrigger>
                      <SelectContent>{COUNTRIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Preferred Location</Label><Input placeholder="Hyderabad / Bangalore / Remote..." value={f.preferred_location} onChange={e=>set("preferred_location",e.target.value)} data-testid="register-preferred-location"/></div>
              </>
            )}

            {step === 2 && (
              <>
                <div><Label>Password *</Label><Input type="password" required value={f.password} onChange={e=>set("password",e.target.value)} data-testid="register-password"/></div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${strengthColor}`} style={{width: `${(strength/5)*100}%`}}/>
                  </div>
                  <span className="text-xs text-slate-500 w-20 text-right">{f.password ? strengthLabel : ""}</span>
                </div>
                <p className="text-xs text-slate-500">Use 8+ characters with uppercase, lowercase, a number and a symbol.</p>
                <div><Label>Confirm Password *</Label><Input type="password" required value={f.confirm_password} onChange={e=>set("confirm_password",e.target.value)} data-testid="register-confirm-password"/></div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Professional Status</Label>
                    <Select value={f.professional_status} onValueChange={(v)=>set("professional_status",v)}>
                      <SelectTrigger data-testid="register-professional-status"><SelectValue/></SelectTrigger>
                      <SelectContent>{["Student","Fresher","Experienced Professional"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Experience</Label>
                    <Select value={f.experience_category} onValueChange={(v)=>set("experience_category",v)}>
                      <SelectTrigger data-testid="register-experience"><SelectValue/></SelectTrigger>
                      <SelectContent>{EXPERIENCE.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Highest Education</Label>
                  <Select value={f.highest_education} onValueChange={(v)=>set("highest_education",v)}>
                    <SelectTrigger data-testid="register-education"><SelectValue/></SelectTrigger>
                    <SelectContent>{EDUCATION.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label className="mb-2 block">Preferred Domains (choose one or more)</Label>
                  <div className="flex flex-wrap gap-2">
                    {DOMAINS.map(d=>(
                      <button type="button" key={d} onClick={()=>toggleDomain(d)}
                        data-testid={`register-domain-${d.replace(/\W+/g,"-").toLowerCase()}`}
                        className={`px-4 py-2 rounded-full border text-sm transition ${f.preferred_domains.includes(d) ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div><Label>LinkedIn URL</Label><Input placeholder="https://linkedin.com/in/..." value={f.linkedin} onChange={e=>set("linkedin",e.target.value)} data-testid="register-linkedin"/></div>
                  <div><Label>GitHub URL</Label><Input placeholder="https://github.com/..." value={f.github} onChange={e=>set("github",e.target.value)} data-testid="register-github"/></div>
                </div>
                <div className="space-y-3 border-t pt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={f.terms_accepted} onCheckedChange={(v)=>set("terms_accepted", !!v)} data-testid="register-terms"/>
                    <span className="text-sm text-slate-700">I agree to the VS-Solutions Careers <Link to="/terms" className="text-[#2563EB]">Terms & Conditions</Link>. *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={f.privacy_accepted} onCheckedChange={(v)=>set("privacy_accepted", !!v)} data-testid="register-privacy"/>
                    <span className="text-sm text-slate-700">I agree to the <Link to="/privacy" className="text-[#2563EB]">Privacy Policy</Link>. *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={f.marketing_opt_in} onCheckedChange={(v)=>set("marketing_opt_in", !!v)} data-testid="register-marketing"/>
                    <span className="text-sm text-slate-700">Send me relevant job alerts and career updates.</span>
                  </label>
                </div>
              </>
            )}

            <div className="flex justify-between pt-4">
              {step > 1 ? <Button type="button" variant="outline" onClick={back} data-testid="register-back">Back</Button> : <div/>}
              {step < 3 ? (
                <Button type="button" onClick={next} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="register-next">
                  Continue <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              ) : (
                <Button type="submit" disabled={loading} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="register-submit">
                  {loading ? "Sending code..." : "Create Account"} <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              )}
            </div>
          </form>

          <p className="mt-8 text-sm text-slate-500">Already have an account? <Link to="/login" className="text-[#2563EB] font-medium">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
